/************************************************************************************

* UTILITIES

*************************************************************************************/

function debounce(fn, wait) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn.apply(this, args), wait)
  }
}

function formatMoney(cents, format = "") {
  let value = ""
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
  const formatString = format || window.Theme.moneyFormat.money_format

  if (typeof cents == "string") {
    cents = cents.replace(".", "")
  }

  function defaultOption(opt, def) {
    return typeof opt == "undefined" ? def : opt
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2)
    thousands = defaultOption(thousands, ",")
    decimal = defaultOption(decimal, ".")

    if (isNaN(number) || number == null) {
      return 0
    }

    number = (number / 100.0).toFixed(precision)

    var parts = number.split("."),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
      cents = parts[1] ? decimal + parts[1] : ""

    return dollars + cents
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2)
      break
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0, " ", ".")
      break
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ".")
      break
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ".")
      break
  }

  return formatString.replace(placeholderRegex, value)
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: `application/${type}` }
  }
}

/************************************************************************************

COMPONENT: COLLAPSIBLE TAB

*************************************************************************************/
if (!customElements.get("l-collapsible-tab")) {
  customElements.define(
    "l-collapsible-tab",
    class LTACollapsibleTab extends HTMLElement {
      constructor() {
        super()
      }

      connectedCallback() {
        this.init()
      }

      init() {
        this.setDefaultData()
        this.attachEvents()

        if (this.getAttribute("open") === "true") {
          this.selected = true
          this.classList.add(this.expandedClass)
          this.setExpandedAria()
        } else {
          this.content.style.height = this.collapsedHeight
          this.classList.add(this.collapsedClass)
          this.setCollapsedAria()
        }

        this.content.removeAttribute("hidden")
      }

      setDefaultData() {
        this.expandedClass = "is-expanded"
        this.collapsedClass = "is-collapsed"

        this.transitionendEvent = this.transitionendEventName()

        this.trigger = this.querySelector("[data-trigger]")
        this.content = this.querySelector("[data-content]")

        this.collapsedHeight = "0px"
        this.defaultElements = ["a", "button", "input:not(.focus-none)", "[data-trigger]"]
        this.oneAtATime = true

        this.dataset.oneOpen === "false" && (this.oneAtATime = false)
      }

      attachEvents() {
        this.trigger.addEventListener("click", (e) => this.toggle(e))
      }

      toggle(e) {
        e.preventDefault()

        this.selected ? this.close() : this.open()

        /* Close another tab and open only the current tab. */
        if (this.selected && this.oneAtATime) {
          const tabsContainer = this.closest("[data-collapsible-wrapper]")
          const collapsibleTabs = tabsContainer?.querySelectorAll("l-collapsible-tab")

          collapsibleTabs?.length &&
            collapsibleTabs.forEach((tab) => {
              tab !== this && tab.selected && tabsContainer !== tab && tab.close()
            })
        }
      }

      open() {
        this.selected = true
        this.expand()
        this.setAttribute("open", true)
      }

      close() {
        this.selected = false
        this.collapse()
        this.removeAttribute("open")
      }

      expand() {
        const onTransitionEnd = ((event) => {
          if (event.target === this.content) {
            this.content.removeEventListener(this.transitionendEvent, onTransitionEnd)

            this.isOpen &&
              requestAnimationFrame(() => {
                this.content.style.transition = "0"
                this.content.style.height = "auto"

                requestAnimationFrame(() => {
                  this.content.style.height = null
                  this.content.style.transition = null
                  this.setExpandedAria()
                  this.trySetTabIndex(this.content, 0)
                })
              })
          }
        }).bind(this)

        this.content.addEventListener(this.transitionendEvent, onTransitionEnd)
        this.isOpen = true
        this.classList.add(this.expandedClass)
        this.classList.remove(this.collapsedClass)
        this.content.style.height = this.content.scrollHeight + "px"
      }

      collapse() {
        const onTransitionEnd = ((event) => {
          if (event.target === this.content) {
            this.content.removeEventListener(this.transitionendEvent, onTransitionEnd)

            if (!this.isOpen) {
              this.setCollapsedAria()
              this.classList.add(this.collapsedClass)
              this.trySetTabIndex(this.content, -1)
            }
          }
        }).bind(this)

        this.content.addEventListener(this.transitionendEvent, onTransitionEnd)
        this.isOpen = false
        this.classList.remove(this.expandedClass)

        requestAnimationFrame(() => {
          this.content.style.transition = "0"
          this.content.style.height = this.content.scrollHeight + "px"

          requestAnimationFrame(() => {
            this.content.style.transition = null
            this.content.style.height = this.collapsedHeight
          })
        })
      }

      destroy() {
        this.trigger && this.trigger.removeEventListener("click", (e) => this.toggle(e))
        this.content && this.content.removeAttribute("aria-hidden")
        this.content && (this.content.style.height = "auto")

        this.classList.remove(this.expandedClass, this.collapsedClass)
        this.removeAttribute("open")
      }

      transitionendEventName() {
        let eventName,
          testElement = document.createElement("div"),
          eventNames = {
            transition: "transitionend",
            OTransition: "otransitionend",
            MozTransition: "transitionend",
            WebkitTransition: "webkitTransitionEnd"
          }

        for (eventName in eventNames) {
          if (eventNames.hasOwnProperty(eventName) && testElement.style[eventName] !== false) {
            return eventNames[eventName]
          }
        }
      }

      trySetTabIndex(parentElement, tabIndex) {
        const elements = parentElement.querySelectorAll(this.defaultElements)

        elements &&
          elements.forEach((e) => {
            e.setAttribute("tabindex", tabIndex)
          })
      }

      setExpandedAria() {
        this.trigger.setAttribute("aria-expanded", "true")
        this.content.setAttribute("aria-hidden", "false")
      }

      setCollapsedAria() {
        this.trigger.setAttribute("aria-expanded", "false")
        this.content.setAttribute("aria-hidden", "true")
      }
    }
  )
}

/************************************************************************************

COMPONENT: SWIPER CAROUSEL

*************************************************************************************/
if (!customElements.get("l-swiper-carousel")) {
  customElements.define(
    "l-swiper-carousel",
    class LTASwiperCarousel extends HTMLElement {
      constructor() {
        super()
      }

      connectedCallback() {
        // Check if Swiper is already loaded
        if (window.Swiper) {
          this.setupCarousel()

          return
        }

        document.addEventListener("carousel:loaded", () => {
          this.setupCarousel()
        })
      }

      setupCarousel() {
        this.carouselConfig = JSON.parse(this.querySelector("[data-carousel-config]").textContent)

        if (!this.carouselConfig) return

        this.classList.remove("is-placeholder")
        this.querySelectorAll(".swiper-slide").forEach((swiperSlide) =>
          swiperSlide.classList.remove("l-hidden")
        )

        this.swiperCarousel = new window.Swiper(this, this.carouselConfig)
      }

      slideTo(index = 0) {
        this.swiperCarousel.slideTo(index)
      }
    }
  )
}

/************************************************************************************

[PRODUCT PAGE]:
  + [COMPONENT]: PRODUCT STICKY ATC

*************************************************************************************/
if (!customElements.get("l-product-sticky-atc")) {
  customElements.define(
    "l-product-sticky-atc",
    class LTAProductStickyATC extends HTMLElement {
      constructor() {
        super()
      }

      connectedCallback() {
        // Elements
        this.mainProductPageEl = document.getElementById("MainProduct")
        this.submitATCEl = this.mainProductPageEl.querySelector("[data-product-atc]")
        this.submitFormEl = this.mainProductPageEl.querySelector("[data-product-form]")

        this.atcBtnEl = this.querySelector("[data-atc-btn]")
        this.priceEl = this.querySelector("[data-product-price]")

        if (!this.submitATCEl) {
          this.classList.remove("is-active")

          return
        }

        // Events
        this.onScrollEvents()
        this.addToCartEvent()
        this.onVariantChangeEvent()
      }

      onScrollEvents() {
        const detectVisible = () => {
          const rect = this.submitATCEl.getBoundingClientRect()
          const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight
          const isShowSticky = window.scrollY > rect.bottom && !isVisible

          this.classList.toggle("is-active", isShowSticky)
        }

        detectVisible()

        window.addEventListener(
          "scroll",
          debounce(() => {
            detectVisible()
          }, 50)
        )
      }

      addToCartEvent() {
        this.atcBtnEl.addEventListener("click", () => {
          this.submitFormEl.dispatchEvent(new CustomEvent("submit", { bubbles: true }))
          this.submitATCEl.dispatchEvent(new CustomEvent("click", { bubbles: true }))

          this.submitFormEl.submit()
          this.atcBtnEl.classList.add("is-loading")

          debounce(() => {
            this.atcBtnEl.classList.remove("is-loading")
          }, 2000)()
        })
      }

      onVariantChangeEvent() {
        document.addEventListener("variant-change", (event) => {
          const variant = event.detail.variant
          const product = event.detail.product

          if (product) {
            this.priceEl.innerHTML = formatMoney(variant.price)
          }
        })
      }
    }
  )
}

/************************************************************************************

CART PAGE: FREE SHIPPING THRESHOLD

*************************************************************************************/
if (!customElements.get("l-free-shipping-threshold")) {
  customElements.define(
    "l-free-shipping-threshold",
    class LTAFreeShippingThreshold extends HTMLElement {
      constructor() {
        super()
      }

      connectedCallback() {
        this.thresholdValue = parseFloat(this.dataset.thresholdValue)
        this.activeMsg = this.querySelector("[data-active-message]").innerHTML
        this.inActiveMsg = this.querySelector("[data-in-active-message]").innerHTML

        this.textEl = this.querySelector("[data-text]")

        window.addEventListener("cartcount:update", (event) => {
          const cart = event.detail
          const cartTotal = parseFloat(cart.total_price)

          const percentage = (cartTotal / this.thresholdValue) * 100

          this.textEl.innerHTML =
            percentage > 100
              ? this.activeMsg
              : this.inActiveMsg.replace("[amount]", formatMoney(this.thresholdValue - cartTotal))

          this.style.setProperty("--l-percentage", `${percentage > 100 ? 100 : percentage}%`)
        })
      }
    }
  )
}

if (!customElements.get("l-cart-note")) {
  customElements.define(
    "l-cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super()

        this.addEventListener(
          "input",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value })

            console.log(event.target.value)

            fetch(`${window.Theme.routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          }, 300)
        )
      }
    }
  )
}

/************************************************************************************

* PRODUCT PAGE

*************************************************************************************/


/********************************
 * [COMPONENT]: L-PRODUCT-VARIANTs
 *********************************/

if (!customElements.get("l-product-variants")) {
  customElements.define(
    "l-product-variants",
    class LTAProductVariants extends HTMLElement {
      constructor() {
        super()
      }

      connectedCallback() {
        this.defaultVariantSelectEl = document.getElementById("ProductDefaultVariantSelector")

        this.addEventListener("change", (event) => {
          const variantId = event.target.value

          this.defaultVariantSelectEl.value = variantId
          this.defaultVariantSelectEl.dispatchEvent(new Event("change", { bubbles: true }))
        })
      }
    }
  )
}

/********************************
 * [COMPONENT]: L-PRODUCT-VARIANT-INCLUDES
 *********************************/

if (!customElements.get("l-product-variants-includes")) {
  customElements.define(
    "l-product-variants-includes",
    class LTAProductVariantsIncludes extends HTMLElement {
      constructor() {
        super()
      }

      connectedCallback() {
        this.variantEls = this.querySelectorAll("[data-variant]")

        document.addEventListener("variant-change", (event) => {
          const variant = event.detail.variant
          const product = event.detail.product

          if (product) {
            this.variantEls.forEach((variantEl) => {
              const currentVariantId = variantEl.dataset.variant

              variantEl.classList.toggle("is-hidden", variant.id != currentVariantId)
            })
          }
        })
      }
    }
  )
}


/********************************
 * [COMPONENT]: L-PRODUCT-VARIANT-INCLUDES
 *********************************/

if (!customElements.get("l-product-mobile-header")) {
  customElements.define(
    "l-product-mobile-header",
    class LTAProductMobileHeader extends HTMLElement {
      constructor() {
        super()
      }

      connectedCallback() {

        document.addEventListener("variant:price-updated", (event) => {
          const { variant, hasComparePrice, price, compareAtPrice, formattedPrice, formattedCompareAtPrice, compareAtPriceHTML, currentPriceHTML } = event.detail

          const savePercentage = Math.round(((compareAtPrice - price) / compareAtPrice) * 100)

          this.badgeSaleEl = this.querySelector("[data-badge-sales]")
          this.percentageEls = this.querySelectorAll("[data-price-percent-saved]")

          this.comparePriceContainerEl = this.querySelector("[data-price-compare-container]")
          this.priceContainerEl = this.querySelector("[data-price-compare-container]")

          if (this.comparePriceContainerEl) this.comparePriceContainerEl.innerHTML = currentPriceHTML
          if (this.comparePriceContainerEl) this.comparePriceContainerEl.innerHTML = compareAtPriceHTML

          this.comparePriceEl = this.querySelector("[data-price-compare-container] [data-price-compare], [data-price-compare-container] [data-price-original]")
          this.priceEl = this.querySelector("[data-price-container] [data-price]")

          if (this.priceEl) this.priceEl.innerHTML = formattedPrice
          if (this.comparePriceEl) this.comparePriceEl.innerHTML = compareAtPrice ? formattedCompareAtPrice : ''

          if (this.badgeSaleEl) {
            this.percentageEls.forEach((percentageEl) => {
              percentageEl.innerHTML = savePercentage > 0 ? `${savePercentage}%` : ""
            })

            this.badgeSaleEl.classList.toggle("l-hidden", !hasComparePrice)
            this.badgeSaleEl.style.display = hasComparePrice ? "inline-block" : "none"
          }
          if (this.comparePriceContainerEl) {
            this.comparePriceEl.classList.toggle("price__compare-at--single", hasComparePrice)
            this.comparePriceContainerEl.classList.toggle("visible", hasComparePrice)
          }
        })
      }
    }
  )
}

/********************************
 * [COMPONENT]: L-PRODUCT-PAYMENT-TERMS
 *********************************/

if (!customElements.get("l-product-payment-terms")) {
  customElements.define(
    "l-product-payment-terms",
    class LTAProductPaymentTerms extends HTMLElement {
      constructor() {
        super()
      }

      connectedCallback() {
        document.addEventListener("variant-change", (event) => {
          const variantId = event.detail.variant?.id
          if (!variantId) return

          const inputEl = this.querySelector('input[name="id"]')

          inputEl.value = variantId
          inputEl.dispatchEvent(new Event("change", { bubbles: true }))
          inputEl.dispatchEvent(new Event("input", { bubbles: true }))
        })
      }
    }
  )
}

document.addEventListener("DOMContentLoaded", () => {
  const headerSearchEl = document.querySelector("[data-mobile-search-button]")
  const drawerFakeSearch = document.querySelector("[data-mobile-search-menu-button]")

  drawerFakeSearch.addEventListener("click", () => {
    headerSearchEl.dispatchEvent(new CustomEvent("click", { bubbles: true }))
  })
})



